import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Siren, ShieldCheck, CheckCircle2, Loader2, Phone, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SERVICE_TYPES, BREAKDOWN_TYPE, BMW_TYPE, PHONES, PHONE_DISPLAY, PHONE2_DISPLAY, isValidEmail, isValidPhone } from "@/lib/site";

const EMPTY = { name: "", phone: "", email: "", vehicle: "", service_type: BREAKDOWN_TYPE, preferred_date: "", message: "" };

const inputCls =
  "w-full border border-white/10 bg-[#12151C] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";
const errorInputCls = "border-red-500/60 focus:border-red-500";

export const FieldError = ({ id, message }) =>
  message ? (
    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-red-400" data-testid={`${id}-error`} role="alert">
      <AlertCircle size={12} /> {message}
    </p>
  ) : null;

const validateBooking = (form) => {
  const errors = {};
  if (form.name.trim().length < 2) errors.name = "Please enter your full name.";
  if (!form.phone.trim()) errors.phone = "Please enter a phone number.";
  else if (!isValidPhone(form.phone)) errors.phone = "Enter a valid phone number (digits only, e.g. 021 123 4567).";
  if (!form.email.trim()) errors.email = "Please enter your email address.";
  else if (!isValidEmail(form.email)) errors.email = "Enter a valid email address (e.g. you@example.com).";
  if (form.vehicle.trim().length < 2) errors.vehicle = "Tell us which vehicle this is for.";
  return errors;
};

export const BookingSection = ({ selectedService }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (selectedService) setForm((f) => ({ ...f, service_type: selectedService }));
  }, [selectedService]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateBooking(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.preferred_date) delete payload.preferred_date;
      if (!payload.message) delete payload.message;
      const { data } = await api.post("/bookings", payload);
      setConfirmation(data);
      setForm(EMPTY);
      toast.success(data.is_breakdown ? "Breakdown request sent — flagged URGENT" : "Booking request sent");
    } catch (err) {
      toast.error(formatApiError(err, "Booking failed. Please try again or call us directly."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32" data-testid="booking-section">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500">Book the workshop</p>
          <h2 className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            Tell us what's wrong. We'll take it from there.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            Every request lands directly on the workshop floor. Breakdowns are flagged urgent the
            moment they arrive, and BMW diagnostic searches go straight to our master technician.
          </p>

          <div className="mt-10 border border-red-500/30 bg-red-500/5 p-6">
            <div className="flex items-center gap-3">
              <Siren size={20} className="animate-pulse-glow text-red-400" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-red-400">Broken down right now?</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Don't wait on a form. Call the recovery line and we'll talk you through the next ten minutes.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              {PHONES.map((p) => (
                <a
                  key={p.id}
                  href={p.tel}
                  data-testid={`booking-emergency-call-${p.id}`}
                  className="inline-flex items-center gap-3 border border-red-500/60 px-5 py-3 font-mono text-sm tracking-wider text-red-300 transition-colors duration-200 hover:bg-red-500 hover:text-white"
                >
                  <Phone size={16} /> {p.display}
                </a>
              ))}
            </div>
          </div>

          <ul className="mt-10 space-y-4">
            {[
              "Fixed quote before any spanner turns",
              "OEM or better parts, always itemised",
              "Written diagnostic report with every job",
            ].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                <CheckCircle2 size={16} className="shrink-0 text-amber-500" /> {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-white/10 bg-[#12151C] p-6 sm:p-10" data-testid="booking-form-card">
          <AnimatePresence mode="wait">
            {confirmation ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex h-full flex-col items-start justify-center py-10"
                data-testid="booking-success-panel"
              >
                <CheckCircle2 size={44} className="text-amber-500" />
                <h3 className="mt-6 font-display text-3xl uppercase tracking-tight text-slate-50">
                  {confirmation.is_breakdown ? "Breakdown request received" : "Booking request received"}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid="booking-success-message">
                  Thanks {confirmation.name} — we've got your {confirmation.service_type.toLowerCase()} request for your {confirmation.vehicle}. Your reference is{" "}
                  <span className="font-mono text-amber-500" data-testid="booking-reference">
                    {confirmation.id.slice(0, 8).toUpperCase()}
                  </span>
                  . Our team will call you back on {confirmation.phone} shortly{confirmation.is_breakdown ? " — your breakdown has been flagged URGENT and jumps the queue" : ""}.
                </p>
                <button
                  onClick={() => setConfirmation(null)}
                  data-testid="booking-new-request"
                  className="mt-8 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-amber-600 hover:text-amber-500"
                >
                  Make another request
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" noValidate exit={{ opacity: 0, y: -12 }} onSubmit={submit} className="space-y-5" data-testid="booking-form">
                <AnimatePresence>
                  {form.service_type === BREAKDOWN_TYPE && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs leading-relaxed text-red-300"
                      data-testid="breakdown-alert"
                    >
                      <strong className="font-mono uppercase tracking-[0.15em]">Urgent breakdown flagged.</strong>
                      <br />
                      This request is marked priority and jumps the queue. For immediate roadside help call {PHONE_DISPLAY} or {PHONE2_DISPLAY}.
                    </motion.div>
                  )}
                  {form.service_type === BMW_TYPE && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden border border-blue-500/50 bg-blue-500/10 px-4 py-3 text-xs leading-relaxed text-blue-300"
                      data-testid="bmw-notice"
                    >
                      <strong className="font-mono uppercase tracking-[0.15em]">OEM diagnostic guarantee.</strong>
                      <br />
                      Your search goes directly to our BMW master technician with factory-level ISTA+ equipment.
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Service required</label>
                  <select value={form.service_type} onChange={set("service_type")} className={inputCls} data-testid="booking-service-select">
                    {SERVICE_TYPES.map((s) => (
                      <option key={s} value={s} className="bg-[#12151C]">{s}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Full name</label>
                    <input value={form.name} onChange={set("name")} placeholder="Alex Turner" className={`${inputCls} ${errors.name ? errorInputCls : ""}`} aria-invalid={!!errors.name} data-testid="booking-name-input" />
                    <FieldError id="booking-name" message={errors.name} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Phone</label>
                    <input value={form.phone} onChange={set("phone")} placeholder="021 123 4567" className={`${inputCls} ${errors.phone ? errorInputCls : ""}`} aria-invalid={!!errors.phone} data-testid="booking-phone-input" />
                    <FieldError id="booking-phone" message={errors.phone} />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</label>
                    <input type="text" inputMode="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={`${inputCls} ${errors.email ? errorInputCls : ""}`} aria-invalid={!!errors.email} data-testid="booking-email-input" />
                    <FieldError id="booking-email" message={errors.email} />
                  </div>
                  <div>
                    <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Vehicle</label>
                    <input value={form.vehicle} onChange={set("vehicle")} placeholder="BMW 320i (2020)" className={`${inputCls} ${errors.vehicle ? errorInputCls : ""}`} aria-invalid={!!errors.vehicle} data-testid="booking-vehicle-input" />
                    <FieldError id="booking-vehicle" message={errors.vehicle} />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Preferred date (optional)</label>
                  <input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="booking-date-input" />
                </div>

                <div>
                  <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">What's happening with the car?</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Describe symptoms, warning lights, noises…"
                    className={`${inputCls} resize-none`}
                    data-testid="booking-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit-button"
                  className="flex w-full items-center justify-center gap-3 bg-amber-600 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500 disabled:opacity-60"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  {submitting ? "Sending…" : "Request callback"}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
